import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetTagsWithHeadlines } from '@root/modules/tags/types/tag';
import getTagsWithHeadlines from '@root/modules/tags/graphql/getTagsWithHeadlines.graphql';

import { getRouteParams } from '@root/common/utils/url';
import { getPageOffset } from '@root/common/utils/getPageOffset';
import { dataLoader, RedisDataLoaderOptions } from '@root/libs/redis/dataloader/dataLoader';
import { convertArrayToObject } from '@root/common/utils/helpers';
import { Tag } from '@root/common/types/fragment';

type ServiceVariables = { id?: number | string; slug?: string; page?: string; language?: string };
type FetchVariables = GetTagsWithHeadlines['variables'];
type Response = GetTagsWithHeadlines['data'];

interface FetchTagsReturn {
  tag: Response['tags']['data'][0];
  headlines: Response['headlines'];
}

export default class TagWithHeadlines extends Service implements ServiceInterface {
  private retriedFailedRequest = false;

  private fixVariables({ slug, language, page }: ServiceVariables) {
    const paramsString = getRouteParams({ slug, desiredIdType: 'string' });

    const limit = 20;
    const offset = getPageOffset(limit, page);
    const variables: FetchVariables = {
      offset,
      limit,
      slug: paramsString.slug,
      authorLanguage: language,
      tagLanguage: language,
    };
    return variables;
  }

  private async fetchTagWithHeadlines(variables: FetchVariables): Promise<FetchTagsReturn | undefined> {
    try {
      const options = Object.assign({ query: getTagsWithHeadlines }, { variables });
      const dataLoaderOptions: RedisDataLoaderOptions = {
        remote: {
          keyPrefix: 'tag',
        },
      };
      // Redis dataloader batch function accepts only array in argument
      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };

      if (variables.offset !== 0) {
        dataLoaderOptions.remote!.gracePeriodMs = 600000; // 10 minutes for pager
      }
      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response?.tags?.data.length) {
        this.handleError({ message: 'tag.error.not_found', statusCode: 404 });
        return;
      }

      const tag = response.tags.data[0];

      // Convert attributes array to object
      const attributes = convertArrayToObject<Tag['attributes']>(tag.attributes);
      const tagWithAttributes = { ...tag, ...attributes };

      return {
        tag: tagWithAttributes,
        headlines: response.headlines,
      };
    } catch (e) {
      // Retry failed request one time
      if (!this.retriedFailedRequest) {
        this.retriedFailedRequest = true;

        return await this.fetchTagWithHeadlines(variables);
      }

      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: ServiceVariables) {
    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchTagWithHeadlines(fixedVariables);

    return response;
  }
}

export function fetchTagWithHeadlines(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const tagWithHeadlines = new TagWithHeadlines({ req });
  return tagWithHeadlines.fetch(variables);
}
